import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { adminApiFetch } from "@/shared/api/adminApiFetch";
import { QUERY_CACHE_CLEAR_INTERVAL_MINUTES, STATUS, TENANT_STATUS } from "@/constants";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type CreateTenantPayload = {
  tenant_name: string;
};

export type TenantCreateResponse = {
  job_id: string;
  tenant_id: number;
};

export type CacheClearResponse = {
  scope: "tenant" | "all";
  tenant_id: number | null;
  deleted_keys: number;
};

export type UpdateQueryCacheIntervalPayload = {
  tenantId: number;
  intervalMinutes: number;
};

export type QueryCacheSettingsResponse = {
  tenant_id: number;
  interval_minutes: number;
  next_clear_at: string;
};

/**
 * Partial update: an omitted field is left alone rather than turned off.
 *
 * `enabledJobTypes` is the whole list, not a delta — the caller sends the set it wants. Sending a
 * non-empty list requires scheduled jobs to be on, in the same request or already; turning
 * scheduled jobs off clears the list server-side, which is why the reducer takes both back from
 * the response rather than assuming only what it sent changed.
 */
export type UpdateTenantSchedulingPayload = {
  tenantId: number;
  nuggetScheduleEnabled?: boolean;
  scheduledJobsEnabled?: boolean;
  enabledJobTypes?: string[];
  /** Five-field UTC cron for the nightly Learning History sweep, e.g. `0 0 * * *`. */
  userLearningSweepSchedule?: string;
};

export type TenantSchedulingSettingsResponse = {
  tenant_id: number;
  nugget_schedule_enabled: boolean;
  scheduled_jobs_enabled: boolean;
  enabled_job_types: string[];
  /**
   * Null while the tenant is still provisioning (no database to read) or was never seeded the
   * sweep definition. Unlike the fields above it lives in the tenant's own `scheduled_jobs` row,
   * which is why the tenants list does not carry it — reading it for every row would mean one
   * cross-database query per tenant.
   */
  user_learning_sweep_schedule: string | null;
};

/**
 * The whole grant list, not a delta — the screen already shows the current set, so sending the
 * intended set avoids two staff members racing on add-then-remove.
 */
export type UpdateTenantS3BucketGrantsPayload = {
  tenantId: number;
  allowedBuckets: string[];
};

export type TenantS3BucketGrantsResponse = {
  tenant_id: number;
  allowed_buckets: string[];
};

export type TenantStatus = (typeof TENANT_STATUS)[keyof typeof TENANT_STATUS];

export type TenantSummary = {
  id: number;
  name: string;
  status: TenantStatus;
  created_at: string;
  query_cache_clear_interval_minutes: number;
  nugget_schedule_enabled: boolean;
  scheduled_jobs_enabled: boolean;
  /** Job types Olive staff has enabled. Only meaningful while `scheduled_jobs_enabled` is true. */
  enabled_job_types: string[];
};

export type TenantsState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  tenants: TenantSummary[];
};

export const tenantsInitialState: TenantsState = {
  status: STATUS.IDLE,
  error: null,
  tenants: [],
};

const tenantStatusValues = new Set<string>(Object.values(TENANT_STATUS));

function isTenantStatus(value: unknown): value is TenantStatus {
  return typeof value === "string" && tenantStatusValues.has(value);
}

function parseTenantSummaryRow(value: unknown): TenantSummary | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const row = value as Record<string, unknown>;
  if (
    typeof row.tenant_id !== "number" ||
    typeof row.tenant_name !== "string" ||
    !isTenantStatus(row.status) ||
    typeof row.created_at !== "string"
  ) {
    return null;
  }
  return {
    id: row.tenant_id,
    name: row.tenant_name,
    status: row.status,
    created_at: row.created_at,
    query_cache_clear_interval_minutes:
      typeof row.query_cache_clear_interval_minutes === "number"
        ? row.query_cache_clear_interval_minutes
        : QUERY_CACHE_CLEAR_INTERVAL_MINUTES.DEFAULT,
    // Default off, matching the column default: a tenant is never scheduled by accident.
    nugget_schedule_enabled: row.nugget_schedule_enabled === true,
    scheduled_jobs_enabled: row.scheduled_jobs_enabled === true,
    enabled_job_types: parseJobTypes(row.enabled_job_types),
  };
}

/** Empty for anything that is not an array of strings — same fail-closed default as the booleans. */
function parseJobTypes(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((jobType): jobType is string => typeof jobType === "string");
}

function parseTenantsResponse(data: unknown): TenantSummary[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  const tenants = data.map(parseTenantSummaryRow);
  if (tenants.some((tenant) => tenant === null)) {
    return null;
  }
  return tenants as TenantSummary[];
}

function isTenantCreateResponse(value: unknown): value is TenantCreateResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    (typeof row.job_id === "string" || typeof row.job_id === "number") &&
    typeof row.tenant_id === "number"
  );
}

function isCacheClearResponse(value: unknown): value is CacheClearResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    (row.scope === "tenant" || row.scope === "all") &&
    (row.tenant_id === null || typeof row.tenant_id === "number") &&
    typeof row.deleted_keys === "number"
  );
}

export const createTenant = createAsyncThunk<
  TenantCreateResponse,
  CreateTenantPayload,
  { rejectValue: string }
>("tenants/create", async (payload, { rejectWithValue }) => {
  try {
    const res = await adminApiFetch("/v1/tenants", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isTenantCreateResponse(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return {
      job_id: String(data.job_id),
      tenant_id: data.tenant_id,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const clearTenantCache = createAsyncThunk<
  CacheClearResponse,
  number,
  { rejectValue: string }
>("tenants/clearCache", async (tenantId, { rejectWithValue }) => {
  try {
    const res = await adminApiFetch("/v1/tenants/cache/clear", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenant_id: tenantId }),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isCacheClearResponse(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const clearAllTenantsCache = createAsyncThunk<
  CacheClearResponse,
  void,
  { rejectValue: string }
>("tenants/clearAllCache", async (_, { rejectWithValue }) => {
  try {
    const res = await adminApiFetch("/v1/tenants/cache/clear", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isCacheClearResponse(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

function isQueryCacheSettingsResponse(
  value: unknown,
): value is QueryCacheSettingsResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.tenant_id === "number" &&
    typeof row.interval_minutes === "number" &&
    typeof row.next_clear_at === "string"
  );
}

export const updateTenantQueryCacheInterval = createAsyncThunk<
  QueryCacheSettingsResponse,
  UpdateQueryCacheIntervalPayload,
  { rejectValue: string }
>(
  "tenants/updateQueryCacheInterval",
  async ({ tenantId, intervalMinutes }, { rejectWithValue }) => {
    try {
      const res = await adminApiFetch(`/v1/tenants/${tenantId}/query-cache`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ interval_minutes: intervalMinutes }),
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!isQueryCacheSettingsResponse(data)) {
        return rejectWithValue("Unexpected response shape");
      }
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

function isTenantSchedulingSettingsResponse(
  value: unknown,
): value is TenantSchedulingSettingsResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.tenant_id === "number" &&
    typeof row.nugget_schedule_enabled === "boolean" &&
    typeof row.scheduled_jobs_enabled === "boolean" &&
    Array.isArray(row.enabled_job_types) &&
    row.enabled_job_types.every((jobType) => typeof jobType === "string") &&
    (row.user_learning_sweep_schedule === null ||
      typeof row.user_learning_sweep_schedule === "string")
  );
}

export const fetchTenantScheduling = createAsyncThunk<
  TenantSchedulingSettingsResponse,
  number,
  { rejectValue: string }
>("tenants/fetchScheduling", async (tenantId, { rejectWithValue }) => {
  try {
    const res = await adminApiFetch(`/v1/tenants/${tenantId}/scheduling`);
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isTenantSchedulingSettingsResponse(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const updateTenantScheduling = createAsyncThunk<
  TenantSchedulingSettingsResponse,
  UpdateTenantSchedulingPayload,
  { rejectValue: string }
>(
  "tenants/updateScheduling",
  async (
    {
      tenantId,
      nuggetScheduleEnabled,
      scheduledJobsEnabled,
      enabledJobTypes,
      userLearningSweepSchedule,
    },
    { rejectWithValue },
  ) => {
    try {
      const body: Record<string, boolean | string | string[]> = {};
      if (nuggetScheduleEnabled !== undefined) {
        body.nugget_schedule_enabled = nuggetScheduleEnabled;
      }
      if (scheduledJobsEnabled !== undefined) {
        body.scheduled_jobs_enabled = scheduledJobsEnabled;
      }
      if (enabledJobTypes !== undefined) {
        body.enabled_job_types = enabledJobTypes;
      }
      if (userLearningSweepSchedule !== undefined) {
        body.user_learning_sweep_schedule = userLearningSweepSchedule;
      }
      const res = await adminApiFetch(`/v1/tenants/${tenantId}/scheduling`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!isTenantSchedulingSettingsResponse(data)) {
        return rejectWithValue("Unexpected response shape");
      }
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

export const fetchTenants = createAsyncThunk<
  TenantSummary[],
  void,
  { rejectValue: string }
>("tenants/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await adminApiFetch("/v1/tenants", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    const tenants = parseTenantsResponse(data);
    if (!tenants) {
      return rejectWithValue("Unexpected response shape");
    }
    return tenants;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

function isTenantS3BucketGrantsResponse(
  value: unknown,
): value is TenantS3BucketGrantsResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.tenant_id === "number" &&
    Array.isArray(row.allowed_buckets) &&
    row.allowed_buckets.every((bucket) => typeof bucket === "string")
  );
}

/**
 * Which external S3 buckets this tenant may register as a Data Hub connection.
 *
 * Not part of the tenants list response, so the detail page fetches it per tenant rather than
 * reading it off the cached row.
 */
export const fetchTenantS3BucketGrants = createAsyncThunk<
  TenantS3BucketGrantsResponse,
  number,
  { rejectValue: string }
>("tenants/fetchS3BucketGrants", async (tenantId, { rejectWithValue }) => {
  try {
    const res = await adminApiFetch(`/v1/tenants/${tenantId}/s3-bucket-grants`);
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isTenantS3BucketGrantsResponse(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

/** PUT, not PATCH: the resource is one list and the caller always sends the set it wants. */
export const updateTenantS3BucketGrants = createAsyncThunk<
  TenantS3BucketGrantsResponse,
  UpdateTenantS3BucketGrantsPayload,
  { rejectValue: string }
>(
  "tenants/updateS3BucketGrants",
  async ({ tenantId, allowedBuckets }, { rejectWithValue }) => {
    try {
      const res = await adminApiFetch(`/v1/tenants/${tenantId}/s3-bucket-grants`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ allowed_buckets: allowedBuckets }),
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!isTenantS3BucketGrantsResponse(data)) {
        return rejectWithValue("Unexpected response shape");
      }
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

const tenantsSlice = createSlice({
  name: "tenants",
  initialState: tenantsInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchTenants.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchTenants.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.tenants = action.payload;
      })
      .addCase(fetchTenants.rejected, (state) => {
        state.status = STATUS.SUCCEEDED;
        state.tenants = [];
        state.error = null;
      })
      .addCase(updateTenantQueryCacheInterval.fulfilled, (state, action) => {
        const tenant = state.tenants.find(
          (candidate) => candidate.id === action.payload.tenant_id,
        );
        if (tenant) {
          tenant.query_cache_clear_interval_minutes = action.payload.interval_minutes;
        }
      })
      .addCase(updateTenantScheduling.fulfilled, (state, action) => {
        const tenant = state.tenants.find(
          (candidate) => candidate.id === action.payload.tenant_id,
        );
        if (tenant) {
          // All three taken from the response, not just what was sent: turning scheduled jobs off
          // clears the job types server-side, so the reply is the only accurate picture.
          tenant.nugget_schedule_enabled = action.payload.nugget_schedule_enabled;
          tenant.scheduled_jobs_enabled = action.payload.scheduled_jobs_enabled;
          tenant.enabled_job_types = action.payload.enabled_job_types;
        }
      });
  },
});

export const tenantsReducer = tenantsSlice.reducer;

/** One tenant from the cached list, or null when the list has not loaded or has no such id. */
export function selectTenantById(
  state: { tenants: TenantsState },
  tenantId: number | null,
): TenantSummary | null {
  if (tenantId === null) {
    return null;
  }
  return state.tenants.tenants.find((tenant) => tenant.id === tenantId) ?? null;
}
